import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogTitle,
  Card,
  CardHeader,
  DialogActions,
  Button,
  MenuItem,
  DialogContent,
  CardContent,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState, useCallback, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { z } from "zod";

import {
  AutosendOptionType,
  CriteriaType,
  EvaluationCriteriaSchema,
  FirstToMinRoiCriteriaSchema,
  HighestRoiAtTimeCriteriaBaseSchema,
  VariantReachedMinRoiCriteriaBaseSchema,
  EvaluationCriteriaOutput,
  PartialSendEvaluationInputSchema,
} from "@stw/yup-validators";

import {
  ScheduleCampaignPartialInitialSendMutation,
  ScheduleCampaignPartialInitialSendMutationVariables,
  StartCampaignPartialInitialSendMutation,
  StartCampaignPartialInitialSendMutationVariables,
  CancelCampaignPartialEvaluationMutation,
  CancelCampaignPartialEvaluationMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  RhfSelect,
  RhfDateTimePicker,
  RhfCurrencyField,
  RhfPercentageField,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfNumberField } from "~frontend/components/forms/react-hook-form-components/RhfNumberField";
import {
  GET_CAMPAIGN_FOR_VIEW,
  SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND,
  START_CAMPAIGN_PARTIAL_INITIAL_SEND,
  CANCEL_CAMPAIGN_PARTIAL_EVALUATION,
} from "~frontend/graphql/campaigns";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { SendSummaryCard } from "~frontend/routes/subaccounts/campaignView/initialSend/SendSummaryCard";
import { criteriaTypeToLabel } from "~frontend/routes/subaccounts/campaignView/initialSend/constants";
import { CampaignForView } from "~frontend/routes/subaccounts/campaignView/types";
import {
  formatShortTime,
  timezoneDisplayName,
} from "~frontend/util/dateFormat";
import {
  createScheduleForSchema,
  useTextingHours,
} from "~frontend/util/textingHours";
import useMutation from "~frontend/util/useMutation";

function createPartialSendSchema(textingHours: {
  start: string;
  end: string;
  timezone: string;
}) {
  return z.object({
    qty: z.coerce.number().int().min(1, "Must be at least 1").default(0),
    scheduleFor: createScheduleForSchema(textingHours),
    partialSendEvaluation:
      PartialSendEvaluationInputSchema.nullish().default(null),
  });
}

type PartialSendFormOutput = z.infer<
  ReturnType<typeof createPartialSendSchema>
>;

const criteriaSchemaByType = {
  [CriteriaType.FIRST_TO_MIN_ROI]: FirstToMinRoiCriteriaSchema,
  [CriteriaType.VARIANT_REACHED_MIN_ROI]:
    VariantReachedMinRoiCriteriaBaseSchema,
  [CriteriaType.HIGHEST_ROI_AT_TIME]: HighestRoiAtTimeCriteriaBaseSchema,
} as const;

export function PartialSendDialog({
  campaign,
  handleClose,
}: {
  campaign: CampaignForView;
  handleClose: () => void;
}): ReactElement {
  const isSingleVariant =
    campaign.scriptVariants.filter(s => !s.deletedAt).length === 1;
  const isProduction = import.meta.env.STW_ENV_NAME === "production";
  const subaccount = useSubaccountAdminConfig()!;
  const confirm = useConfirm();
  const [canceling, setCanceling] = useState(false);

  const start = campaign.textingHoursStart ?? subaccount.textingHoursStart;
  const end = campaign.textingHoursEnd ?? subaccount.textingHoursEnd;
  const timezone =
    campaign.textingHoursTimezone ?? subaccount.textingHoursTimezone;

  const textingHours = { start, end, timezone };

  const { isInTextingHours } = useTextingHours(textingHours);

  const schema = useMemo(
    () => createPartialSendSchema({ start, end, timezone }),
    [start, end, timezone]
  );

  const activeEvaluation =
    campaign.partialSendEvaluations.find(
      e => e.series === campaign.partialSendEvaluationSeries
    ) ?? null;
  const isReadOnly = !!activeEvaluation;

  const [sendPartial] = useMutation<
    StartCampaignPartialInitialSendMutation,
    StartCampaignPartialInitialSendMutationVariables
  >(START_CAMPAIGN_PARTIAL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to start initial send",
  });

  const [schedulePartial] = useMutation<
    ScheduleCampaignPartialInitialSendMutation,
    ScheduleCampaignPartialInitialSendMutationVariables
  >(SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to schedule initial send",
  });

  const [cancelPartialEvaluation] = useMutation<
    CancelCampaignPartialEvaluationMutation,
    CancelCampaignPartialEvaluationMutationVariables
  >(CANCEL_CAMPAIGN_PARTIAL_EVALUATION, {
    variables: { input: { campaignId: campaign.id } },
    onErrorSnackbarMessage: "Failed to cancel partial send evaluation",
  });

  const { control, getValues, setValue, handleSubmit } = useForm({
    resolver: zodResolver(schema),
    reValidateMode: "onBlur",
    defaultValues: activeEvaluation
      ? {
          qty: activeEvaluation.batchSize,
          partialSendEvaluation: {
            expiresAt: campaign.partialSendEvaluationExpiresAt ?? undefined,
            evaluation: EvaluationCriteriaSchema.parse(
              activeEvaluation.criteria
            ),
          },
        }
      : undefined,
  });

  async function onSubmit(input: PartialSendFormOutput) {
    if (input.scheduleFor) {
      await schedulePartial({
        variables: {
          input: {
            campaignId: campaign.id,
            qty: input.qty,
            scheduleFor: input.scheduleFor,
            partialSendEvaluations: input.partialSendEvaluation,
          },
        },
      });
    } else {
      await sendPartial({
        variables: {
          input: {
            campaignId: campaign.id,
            qty: input.qty,
            partialSendEvaluations: input.partialSendEvaluation,
          },
        },
      });
    }

    handleClose();
  }

  const handleCancel = useCallback(async () => {
    if (canceling) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Cancel partial send evaluation",
      description:
        "Are you sure you want to cancel the partial send evaluation? The campaign will return to ready state.",
      confirmationText: "Cancel evaluation",
      cancellationText: "No",
    });
    if (!confirmed) {
      return;
    }

    setCanceling(true);
    await cancelPartialEvaluation();
    setCanceling(false);
  }, [canceling, confirm, cancelPartialEvaluation]);

  const criteriaType = useWatch({
    control,
    name: "partialSendEvaluation.evaluation.type",
  });
  const scheduleFor = useWatch({ control, name: "scheduleFor" });
  const isScheduled = !!scheduleFor || !isInTextingHours;
  const autosendOption = useWatch({
    control,
    name: "partialSendEvaluation.evaluation.autosendOption",
  });
  const evaluationShape =
    criteriaType && criteriaType in criteriaSchemaByType
      ? criteriaSchemaByType[criteriaType as CriteriaType].shape
      : null;
  const hasAutosend = !!criteriaType;

  function handleToggleAutosend() {
    const partialSendEvaluation = getValues("partialSendEvaluation");
    if (partialSendEvaluation) {
      setValue("partialSendEvaluation", null);
      return;
    }
    const ids = campaign.scriptVariants
      .filter(s => !s.deletedAt)
      .map(s => s.id);
    const evaluation = isSingleVariant
      ? VariantReachedMinRoiCriteriaBaseSchema.partial().parse({
          type: CriteriaType.VARIANT_REACHED_MIN_ROI,
          variantIds: ids,
          autosendOption: {
            type: "SEND_TO_FULL",
          },
        })
      : FirstToMinRoiCriteriaSchema.partial().parse({
          type: CriteriaType.FIRST_TO_MIN_ROI,
          variantIds: ids,
          // set default option to be 3 hours from now (3 * 60)
          evaluationDelayInMin: 180,
          autosendOption: {
            type: "SEND_TO_FULL",
          },
        });
    setValue(
      "partialSendEvaluation.evaluation",
      evaluation as EvaluationCriteriaOutput
    );
  }

  function handleEvaluationTypeChange(newType: unknown) {
    const current = getValues("partialSendEvaluation");
    const merged = { ...(current?.evaluation ?? {}), type: newType };
    const parsed = EvaluationCriteriaSchema.safeParse(merged);

    setValue(
      "partialSendEvaluation.evaluation",
      (parsed.success ? parsed.data : merged) as EvaluationCriteriaOutput,
      { shouldValidate: true }
    );
  }

  const now = DateTime.now();
  const defaultDateTimeProps = {
    inputLabelSlotProps: {
      sx: { fontSize: "14px" },
    },
    minDate: now,
    minTime: now,
    control,
    disabled: isReadOnly,
  };
  const defaultTextNumericProps = {
    slotProps: {
      inputLabel: {
        sx: { fontSize: "14px" },
      },
      input: { sx: { pt: 1 } },
    },
    control,
    disabled: isReadOnly,
  };

  return (
    <Dialog open={true} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0, mb: 0 }}>
        Partial Send on {campaign.name}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Typography sx={{ mb: 2 }} variant="body1">
            Enter how many initial texts you want to send for this portion and
            confirm that the send summary information is correct, then click
            "send" to start your campaign.
          </Typography>
          {isSingleVariant && (
            <Typography sx={{ mb: 2 }}>
              Note that sending across script variants will roughly split your
              list in half, but can't guarantee an exact 50/50 split.
            </Typography>
          )}
          <RhfNumberField
            control={control}
            name={"qty"}
            fullWidth
            label={"Texts to Send"}
            disabled={isReadOnly}
          />
          <RhfDateTimePicker
            name={"scheduleFor"}
            dateLabel={"Initial Send Date"}
            timeLabel={"Initial Send Time"}
            minTime={now}
            minDate={now}
            control={control}
            disabled={isReadOnly}
            optional={isInTextingHours}
          />
          {hasAutosend && (
            <>
              <Typography sx={{ mb: 2 }}>
                Configure your settings to trigger an autosend below. If your
                criteria aren't met, your campaign will pause sending.
              </Typography>
              <Card sx={{ mb: 2 }}>
                <CardHeader
                  title={
                    <Typography variant="body1Bold">
                      <FormattedMessage
                        defaultMessage="Autosend Settings"
                        description="Title for autosend settings menu"
                      />
                    </Typography>
                  }
                />
                <CardContent>
                  {!isSingleVariant && (
                    <RhfSelect
                      name={"partialSendEvaluation.evaluation.type"}
                      label={"Autosend Based On"}
                      fullWidth
                      sx={{
                        mb: 2,
                        pt: 1,
                      }}
                      secondary
                      onChange={handleEvaluationTypeChange}
                      control={control}
                      disabled={isReadOnly}
                    >
                      {(
                        Object.entries(criteriaTypeToLabel) as [
                          CriteriaType,
                          string,
                        ][]
                      )
                        .filter(([_, label]) => !!label)
                        .map(([key, label]) => (
                          <MenuItem key={key} value={key}>
                            {label}
                          </MenuItem>
                        ))}
                    </RhfSelect>
                  )}

                  <FlexBox gap={1} sx={{ mb: 2 }}>
                    <RhfPercentageField
                      fullWidth
                      variant={"standard"}
                      name={"partialSendEvaluation.evaluation.minRoi"}
                      label={"Minimum ROI"}
                      optional={criteriaType !== CriteriaType.FIRST_TO_MIN_ROI}
                      {...defaultTextNumericProps}
                    />
                    <RhfNumberField
                      fullWidth
                      variant={"standard"}
                      name={
                        "partialSendEvaluation.evaluation.minNumberOfDonations"
                      }
                      label={"Minimum Number of Donations"}
                      optional={evaluationShape?.minNumberOfDonations?.isOptional()}
                      {...defaultTextNumericProps}
                    />
                  </FlexBox>
                  <FlexBox gap={2} sx={{ mb: 2 }}>
                    <RhfCurrencyField
                      fullWidth
                      variant="standard"
                      label={"Exclude Donations Over"}
                      name={
                        "partialSendEvaluation.evaluation.excludeDonationsOverInCents"
                      }
                      adornmentOffset={0}
                      optional={evaluationShape?.excludeDonationsOverInCents?.isOptional()}
                      {...defaultTextNumericProps}
                    />
                    {criteriaType === CriteriaType.FIRST_TO_MIN_ROI && (
                      <RhfSelect
                        fullWidth
                        secondary
                        label={"Start Checking for Winner"}
                        name="partialSendEvaluation.evaluation.evaluationDelayInMin"
                        sx={{ pt: 1 }}
                        control={control}
                        disabled={isReadOnly}
                      >
                        <MenuItem key={"immediate"} value={0}>
                          Immediately
                        </MenuItem>
                        {!isProduction && <MenuItem value={5}>5 min</MenuItem>}
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(hours => (
                          <MenuItem key={hours} value={hours * 60}>
                            after {hours} {hours === 1 ? "hour" : "hours"}
                          </MenuItem>
                        ))}
                      </RhfSelect>
                    )}
                  </FlexBox>
                  {criteriaType === CriteriaType.FIRST_TO_MIN_ROI && (
                    <RhfDateTimePicker
                      name={"partialSendEvaluation.evaluation.expiresAt"}
                      dateLabel={`Date to Stop Checking for a Winner`}
                      timeLabel={"Time to Stop Checking for a Winner"}
                      variant="standard"
                      {...defaultDateTimeProps}
                    />
                  )}
                  {criteriaType === CriteriaType.HIGHEST_ROI_AT_TIME && (
                    <RhfDateTimePicker
                      name={"partialSendEvaluation.evaluation.expiresAt"}
                      dateLabel={`Date to Pick a Winner`}
                      timeLabel={"Time to Pick a Winner"}
                      variant="standard"
                      {...defaultDateTimeProps}
                    />
                  )}
                  <RhfSelect
                    name={
                      "partialSendEvaluation.evaluation.autosendOption.type"
                    }
                    label={"Autosend Option"}
                    fullWidth
                    sx={{ pt: 1 }}
                    secondary
                    control={control}
                    disabled={isReadOnly}
                    onChange={value => {
                      // MenuItem value can only be a string so we set the rhf value here.
                      if (value === AutosendOptionType.SEND_TO_PORTION) {
                        setValue(
                          "partialSendEvaluation.evaluation.autosendOption",
                          {
                            type: AutosendOptionType.SEND_TO_PORTION,
                            textsToSend: 1,
                          }
                        );
                      } else {
                        setValue(
                          "partialSendEvaluation.evaluation.autosendOption",
                          { type: AutosendOptionType.SEND_TO_FULL }
                        );
                      }
                    }}
                  >
                    <MenuItem value="SEND_TO_FULL">
                      Send the winner to the remainder of the list
                    </MenuItem>
                    <MenuItem value="SEND_TO_PORTION">
                      Send the winner to a portion of the list, then pause
                    </MenuItem>
                  </RhfSelect>
                  {autosendOption?.type === "SEND_TO_PORTION" && (
                    <RhfNumberField
                      fullWidth
                      variant={"standard"}
                      name={
                        "partialSendEvaluation.evaluation.autosendOption.textsToSend"
                      }
                      label={"Texts to Send"}
                      {...defaultTextNumericProps}
                    />
                  )}
                </CardContent>
              </Card>

              <Typography variant="body2" pb={2}>
                <Typography variant="body2Bold" component="span">
                  Texting Hours:{" "}
                </Typography>
                {formatShortTime(textingHours.start)} -{" "}
                {formatShortTime(textingHours.end)} (
                {timezoneDisplayName(textingHours.timezone)})
                <Typography>
                  A winner will only be sent within your campaign texting hours.
                  If a winner is reached after the end of the texting hours, the
                  send will pause and then resume at the start of the next day's
                  texting hours.
                </Typography>
              </Typography>
              <Typography variant="body2Bold">Don't Send After</Typography>
              <Typography pb={2}>
                If autosend is not started by this date and time, the campaign
                will pause until an admin manually resumes sending or ends the
                campaign.
              </Typography>
              <RhfDateTimePicker
                name={"partialSendEvaluation.expiresAt"}
                dateLabel={"Don't Send After Date"}
                timeLabel={"Don't Send After Time"}
                minTime={now}
                minDate={now}
                control={control}
                disabled={isReadOnly}
              />
            </>
          )}

          <SendSummaryCard campaign={campaign} />

          <DialogActions
            sx={{ justifyContent: "space-between", pl: 0, pr: 0, pt: 2 }}
          >
            {isReadOnly ? (
              <>
                <Button variant="outlined" onClick={handleClose}>
                  CLOSE
                </Button>
                <Button variant="contained" onClick={handleCancel}>
                  Cancel Evaluation
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  onClick={handleToggleAutosend}
                  color="tertiary"
                >
                  {hasAutosend ? "REMOVE AUTOSEND" : "ADD AUTOSEND"}
                </Button>
                <FlexBox gap={1}>
                  <Button variant="outlined" onClick={handleClose}>
                    CLOSE
                  </Button>
                  <Button variant="contained" type="submit">
                    {isScheduled ? "SCHEDULE SEND" : "SEND NOW"}
                  </Button>
                </FlexBox>
              </>
            )}
          </DialogActions>
        </DialogContent>
      </form>
    </Dialog>
  );
}
